import { ChangeDetectionStrategy, Component, computed, inject, output } from '@angular/core';
import { TQueueState, TUploadStatus } from '@simOn/common/upload-queue/models';
import { UploadQueueService } from '@simOn/common/upload-queue/services';
import { SimButtonModule } from '@simOn/ui/sim-button';
import { documentFileIcon, SimIconComponent, SimIconType } from '@simOn/ui/sim-icon';
import { SimProgressBarComponent } from '@simOn/ui/sim-progress-bar';
import { SimSpinnerComponent } from '@simOn/ui/sim-spinner';

/** Right-side panel listing background operations with their progress (opened via the portal service). */
@Component({
  selector: 'layout-operations-in-progress-panel',
  templateUrl: './operations-in-progress-panel.component.html',
  styleUrl: './operations-in-progress-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SimButtonModule, SimIconComponent, SimProgressBarComponent, SimSpinnerComponent]
})
export class OperationsInProgressPanelComponent {
  private readonly _uploadQueue = inject(UploadQueueService);

  readonly closePanel = output<void>();

  protected readonly operations = computed(() => this._uploadQueue.queuedFilesState() ?? []);

  protected readonly inProgressCount = computed(
    () => this.operations().filter((operation) => operation.state === 'IN_PROGRESS').length
  );

  protected readonly errorCount = computed(
    () => this.operations().filter((operation) => operation.state === 'ERROR').length
  );

  /** Item icon — folder for grouped operations, extension icon (PDF, Word, Excel, TIFF) or generic file otherwise. */
  protected operationIcon(operation: TQueueState<TUploadStatus>): SimIconType {
    if (operation.stagesTotal !== undefined) return 'folder';
    return documentFileIcon(operation.name) ?? 'insert_drive_file';
  }

  protected close(): void {
    this.closePanel.emit();
  }

  protected remove(id: string): void {
    this._uploadQueue.removeFromQueue(id);
  }

  protected retry(id: string): void {
    this._uploadQueue.retryOperation(id);
  }

  protected clearErrors(): void {
    this._uploadQueue.clearQueueErrors();
  }
}
